/**
 * RFSDetailPage — the customer's view of a submitted service request.
 * Shows matched providers, quotes, and a threaded messaging area.
 * For a provider, the same route shows the "lead" details + a
 * "Send quote" affordance.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, MapPin, Calendar, Users, Sparkles, IndianRupee, Star, CheckCircle2 } from "lucide-react";
import ShareMenu from "@/components/ShareMenu";

const STATUS_COPY = {
  open: "Finding qualified providers…",
  matched: "Shared with providers — waiting for quotes",
  quoted: "Quotes received — review below",
  booked: "Provider booked",
  completed: "Service completed",
  cancelled: "Request cancelled",
  expired: "Request expired",
};

export default function RFSDetailPage() {
  const { id } = useParams();
  const [rfs, setRfs] = useState(null);
  const [matches, setMatches] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [role, setRole] = useState("customer");
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: m }] = await Promise.all([
        http.get(`/sp-services/rfs/${id}`),
        http.get(`/sp-services/rfs/${id}/messages`),
      ]);
      setRfs(d.rfs);
      setMatches(d.matches || []);
      setQuotes(d.quotes || []);
      setRole(d.viewer_role);
      setMessages(m.messages || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load your request.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try {
      await http.post(`/sp-services/rfs/${id}/messages`, { body: msgText });
      setMsgText("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Message failed.");
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await http.post(`/sp-services/quotes/${quoteId}/accept`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't accept quote.");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (err) return <div className="max-w-2xl mx-auto p-6 text-red-600">{err}</div>;
  if (!rfs) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="rfs-detail-page">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">RFS · {rfs.id.slice(0, 8)}</div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{rfs.title}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="outline" data-testid="rfs-status">{STATUS_COPY[rfs.status] || rfs.status}</Badge>
            <ShareMenu
              url={`/hire/rfs/${rfs.id}`}
              title={rfs.title}
              text={`${rfs.title} · ${rfs.category_name}`}
              entityType="rfs"
              entityId={rfs.id}
              testid="rfs-share"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-1.5"><span className="font-medium">{rfs.category_name}</span></div>
          {rfs.city && <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{rfs.city}</div>}
          {rfs.requested_date && <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{rfs.requested_date}{rfs.requested_time ? ` · ${rfs.requested_time}` : ""}</div>}
          {rfs.structured?.passengers && <div className="flex items-center gap-1.5"><Users className="w-4 h-4" />{rfs.structured.passengers} passenger(s)</div>}
        </div>
        {rfs.description && <p className="mt-3 text-slate-700 whitespace-pre-wrap text-sm">{rfs.description}</p>}
      </div>

      {role === "customer" && matches.length > 0 && (
        <Card className="p-4 sm:p-5 mb-6" data-testid="matches-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <h2 className="font-semibold">We found {matches.length} qualified provider{matches.length !== 1 ? "s" : ""}</h2>
          </div>
          <div className="space-y-3">
            {matches.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-3 border rounded-md bg-slate-50/50">
                <div>
                  <div className="font-medium">{m.provider?.business_name || "Provider"}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    {m.provider?.rating_avg > 0 && <><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{m.provider.rating_avg.toFixed(1)} ({m.provider.rating_count})</>}
                    <span>· {m.provider?.city || m.provider?.region || "—"}</span>
                    <span>· Match score {Math.round(m.score)}</span>
                  </div>
                </div>
                <Badge variant={m.provider_status === "quoted" ? "default" : "outline"}>
                  {m.provider_status === "quoted" ? "Quoted" : m.provider_status === "declined" ? "Declined" : "Notified"}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {role === "customer" && quotes.length > 0 && (
        <Card className="p-4 sm:p-5 mb-6" data-testid="quotes-card">
          <h2 className="font-semibold mb-3">Quotes received</h2>
          <div className="space-y-3">
            {quotes.map(q => (
              <div key={q.id} className="border rounded-md p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{q.provider?.business_name || "Provider"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{q.provider?.city || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold flex items-center gap-0.5">
                      <IndianRupee className="w-4 h-4" />{(q.total / 100).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500">{q.currency}</div>
                  </div>
                </div>
                {q.estimated_duration && <div className="text-sm text-slate-600 mt-2">Estimated: {q.estimated_duration}</div>}
                {q.notes && <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{q.notes}</div>}
                {q.status === "sent" && (
                  <div className="mt-3">
                    <Button size="sm" onClick={() => acceptQuote(q.id)} data-testid={`accept-quote-${q.id}`}>
                      Accept quote
                    </Button>
                  </div>
                )}
                {q.status === "accepted" && (
                  <div className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
                    <CheckCircle2 className="w-4 h-4" />Accepted
                  </div>
                )}
                {q.status === "superseded" && (
                  <div className="mt-3 text-xs text-slate-500">Superseded by another quote</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {role === "provider" && (
        <ProviderLeadPanel rfs={rfs} quotes={quotes} onQuoteSent={load} />
      )}

      <Card className="p-4 sm:p-5" data-testid="messages-card">
        <h2 className="font-semibold mb-3">Messages</h2>
        <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
          {messages.length === 0 && <div className="text-sm text-slate-500">No messages yet. Say hello to get things moving.</div>}
          {messages.map(m => (
            <div key={m.id} className={`text-sm px-3 py-2 rounded-md ${m.sender_role === "system" ? "bg-slate-100 text-slate-700 italic" : m.sender_role === role ? "bg-emerald-50" : "bg-slate-50"}`}>
              <div className="text-xs text-slate-500 mb-0.5">{m.sender_role} · {new Date(m.created_at).toLocaleString()}</div>
              <div className="whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Textarea
            value={msgText}
            onChange={e => setMsgText(e.target.value)}
            placeholder="Type a message..."
            rows={2}
            data-testid="message-input"
          />
          <Button onClick={sendMessage} disabled={!msgText.trim()} data-testid="send-message-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProviderLeadPanel({ rfs, quotes, onQuoteSent }) {
  const [show, setShow] = useState(quotes.length === 0);
  const [q, setQ] = useState({ base_price: "", currency: "INR", travel_fee: "0", notes: "", estimated_duration: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const myQuote = quotes[0]; // provider only sees their own

  const submit = async () => {
    setErr("");
    if (!q.base_price || Number(q.base_price) <= 0) { setErr("Enter a base price."); return; }
    setSubmitting(true);
    try {
      await http.post(`/sp-services/rfs/${rfs.id}/quotes`, {
        base_price: Math.round(Number(q.base_price) * 100),
        travel_fee: Math.round(Number(q.travel_fee || 0) * 100),
        currency: q.currency,
        notes: q.notes || null,
        estimated_duration: q.estimated_duration || null,
      });
      setShow(false);
      onQuoteSent();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send quote.");
    } finally { setSubmitting(false); }
  };

  if (myQuote) {
    return (
      <Card className="p-4 sm:p-5 mb-6 bg-emerald-50/50 border-emerald-200" data-testid="my-quote-card">
        <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="font-semibold">Your quote — {myQuote.status}</span></div>
        <div className="text-2xl font-semibold flex items-center gap-0.5">
          <IndianRupee className="w-5 h-5" />{(myQuote.total / 100).toLocaleString()}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-5 mb-6" data-testid="lead-quote-form">
      <h2 className="font-semibold mb-3">Send your quote</h2>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-sm font-medium">Base price ({q.currency})</label>
          <Input type="number" value={q.base_price} onChange={e => setQ({ ...q, base_price: e.target.value })} data-testid="quote-base-price" />
        </div>
        <div>
          <label className="text-sm font-medium">Travel fee</label>
          <Input type="number" value={q.travel_fee} onChange={e => setQ({ ...q, travel_fee: e.target.value })} />
        </div>
      </div>
      <div className="mb-3">
        <label className="text-sm font-medium">Estimated duration</label>
        <Input value={q.estimated_duration} onChange={e => setQ({ ...q, estimated_duration: e.target.value })} placeholder="e.g. 2 hours" />
      </div>
      <div className="mb-3">
        <label className="text-sm font-medium">Notes</label>
        <Textarea rows={3} value={q.notes} onChange={e => setQ({ ...q, notes: e.target.value })} placeholder="Anything the customer should know..." />
      </div>
      {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
      <Button onClick={submit} disabled={submitting} data-testid="send-quote-btn">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Send quote
      </Button>
    </Card>
  );
}
